"use client";

import { useEffect } from "react";

const FRAME_COUNT = 30;
const FRAME_MS = 120;

/** Runs the favicon's liquid loop: an ink blob that rests clean, then a wave of
    domain-warp distortion (SVG turbulence + displacement, baked to frames) melts
    and reforms it before settling back — a seamless cycle. The static icon.svg is
    the crisp resting blob; this only runs when motion is allowed and the tab is
    visible. Skipped entirely under prefers-reduced-motion.
    Frames are fetched once and cycled as data: URLs — the href swap must never
    be a network URL: with Vercel's default must-revalidate on /public, every
    120ms swap revalidated against the edge and burned the free tier's monthly
    edge-request quota on favicon 304s. */
export function AnimatedFavicon() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let disposed = false;
    let intervalId: number | undefined;
    let play = () => {};

    const pause = () => {
      if (intervalId === undefined) return;
      window.clearInterval(intervalId);
      intervalId = undefined;
    };

    const onVisibility = () => {
      if (document.hidden) pause();
      else play();
    };

    const toDataUrl = async (path: string) => {
      const res = await fetch(path);
      if (!res.ok) throw new Error(path);
      const blob = await res.blob();
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
    };

    Promise.all(
      Array.from({ length: FRAME_COUNT }, (_, i) =>
        toDataUrl(`/favicon-frames/f${i}.png`),
      ),
    )
      .then((frames) => {
        if (disposed) return;

        let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          document.head.appendChild(link);
        }
        const icon = link;
        icon.type = "image/png";

        let frame = 0;
        play = () => {
          if (intervalId !== undefined || disposed) return;
          intervalId = window.setInterval(() => {
            frame = (frame + 1) % FRAME_COUNT;
            icon.href = frames[frame];
          }, FRAME_MS);
        };

        document.addEventListener("visibilitychange", onVisibility);
        if (!document.hidden) play();
      })
      .catch(() => {
        /* offline or a frame 404s: keep the static icon.svg */
      });

    return () => {
      disposed = true;
      pause();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return null;
}
